import { useEffect } from 'react';
import { ActionCreatorWithPayload, AsyncThunk } from '@reduxjs/toolkit';
import { AsyncThunkConfig } from '@reduxjs/toolkit/dist/createAsyncThunk';
import styles from './DashboardMobileItem.module.scss';
import cx from 'classnames';
import { NoContent, PatientList, Spinner, Text } from 'components';
import { Icon, Icons } from 'components/Icon/Icon';
import { RootState } from 'store';
import { selectDashboardLoadingStates } from 'store/dashboard/selectors';
import { useDispatch, useSelector } from 'store/utils';
import {
  DashboardBlocksName,
  GetPatientCasesParams,
  Patient,
  PatientListInfo,
  PatientListInfoResponse,
  User,
} from 'types';

type DashboardItemProps = {
  name: DashboardBlocksName;
  iconName: Icons;
  thunk: AsyncThunk<PatientListInfoResponse, void, AsyncThunkConfig>;
  selectCases: (state: RootState) => Patient[];
  selectInfo: (state: RootState) => PatientListInfo | null;
  selectFilter: (state: RootState) => GetPatientCasesParams;
  setFilter: ActionCreatorWithPayload<GetPatientCasesParams, string>;
  activeTab: string | null;
  setActiveTab(value: string | null): void;
  currentUser: User | null;
};

export const DashboardMobileItem = ({
  name,
  iconName,
  selectCases,
  selectInfo,
  thunk,
  selectFilter,
  setFilter,
  activeTab,
  setActiveTab,
  currentUser,
}: DashboardItemProps) => {
  const patients = useSelector(selectCases);
  const patientListInfo = useSelector(selectInfo);
  const patientListFilter = useSelector(selectFilter);

  const isLoading = useSelector(selectDashboardLoadingStates)[name];

  const dispatch = useDispatch();

  const isCurrentTab = activeTab === name;

  useEffect(() => {
    if (currentUser) {
      dispatch(thunk());
    }
  }, [dispatch, thunk, patientListFilter, currentUser]);

  const onDropdownOpen = () => {
    setActiveTab(name);
  };

  const onDropdownClose = () => {
    setActiveTab(null);
  };

  const isInTreatmentOrFinished = name === 'In treatment' || name === 'Finished' || name === 'Out of treatment';

  const content =
    isLoading && !patients.length ? (
      <div className={styles.loadingContainer}>
        <Spinner isLoading />
      </div>
    ) : patients.length ? (
      <div className={styles.casesContainer}>
        <PatientList
          patients={patients}
          className={styles.list}
          selectFilter={selectFilter}
          selectInfo={selectInfo}
          setFilter={setFilter}
          isLoading={isLoading}
          markSelected={false}
          showPatientCardControls={isInTreatmentOrFinished}
          withPersistentMessageIcon={isInTreatmentOrFinished}
        />
      </div>
    ) : (
      <NoContent pageKey="dashboardList" className={styles.noContent} />
    );

  return (
    <div className={cx(styles.container, { [styles.isOpen]: isCurrentTab })}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Icon name={iconName} />
          <Text type="body-big">{name}</Text>
        </div>
        <div>
          <div className={styles.controls}>
            <div className={styles.counter}>
              <Text
                type="body-big"
                className={cx(styles.counterText, {
                  [styles.counterTextPaddings]: Number(patientListInfo?.totalElements) >= 3,
                })}>
                {patientListInfo?.totalElements ?? 0}
              </Text>
            </div>
            <div className={styles.toggleViewButton} onClick={isCurrentTab ? onDropdownClose : onDropdownOpen}>
              <Icon name="ArrowDown" />
            </div>
          </div>
        </div>
      </div>
      {isCurrentTab && content}
    </div>
  );
};
